import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CalculationStore } from '../../stores/calculation.store';
import { TranslateService } from '@ngx-translate/core';
import { CalculationTitleUpdateFormFactory } from './calculation-title-update-form.factory';
import { Subscription } from 'rxjs';
import { CalculationKeynameEnum } from '../../../../../../../../common/src/enums/calculations/calculation-keyname.enum';
import { CalculationStatusEnum } from '../../../../../../../../common/src/enums/calculations/calculation-status.enum';
import { CalculationResponse } from '../../../../../../../../common/dist/angular/responses/calculation.response';
import { MenuService } from '../../../core/services/menu.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteCalculationComponent } from '../delete-calculation/delete-calculation.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalculationCreationOptions,
} from '../../../../../../../../common/src/options/calculations/calculation-creation.options';
import { DuplicateCalculationComponent } from '../duplicate-calculation/duplicate-calculation.component';
import { UntypedFormControl } from '@angular/forms';
import { AdvancedFormButton } from "@magieno/angular-advanced-forms";
import { CalculationsUtils } from '../../utils/calculations.utils';
import { FormLifecycleStatusEnum } from '@magieno/angular-advanced-forms';
import { LoggingService } from '../../../core/services/logging.service';
import { ImportModalComponent } from '../import-modal/import-modal.component';
import { ImportModalBase } from '../import-modal/import-modal.base';
import { ImportManager } from '../../managers/import.manager';
import { CalculationRunOptions } from '../../../../../../../../common/src/options/calculations/calculation-run.options';
import {
  GrantDelegatedAccessModalComponent,
} from '../grant-delegated-access-modal/grant-delegated-access-modal.component';
import {
  addSeconds,
  differenceInMinutes,
  differenceInSeconds,
  formatISO9075,
  intlFormatDistance,
  subMinutes,
} from 'date-fns';
import { ExportModalFactory } from '../../factories/export-modal.factory';

@Component({
  selector: 'app-calculation-header',
  templateUrl: './calculation-header.component.html',
  styleUrls: ['./calculation-header.component.scss'],
  standalone: false,
})
export class CalculationHeaderComponent implements OnInit, OnDestroy {
  public calculationTitle: string;
  public isTitleInEditingMode: boolean;
  public calculationKeyname: CalculationKeynameEnum;
  public accessCurrentlyGranted = false;
  public remainingCalculationTime: string;
  public isLoading = false;
  public lastModified: Date;
  formServiceOnButtonClickSubscription: Subscription;
  calculationErrorsSubscription: Subscription;
  calculationSubscription: Subscription;
  public calculationTitleUpdateForm = this.calculationTitleUpdateFormFactory.create();
  public numberOfErrors: number = 0;
  public progress: number = 0;
  grantedUntil: string;
  private estimatedTimeOfCompletion: Date;
  private progressInterval: any;
  private calculationResponse: CalculationResponse;
  private progressDurationInSeconds = 45;

  constructor(public readonly menuService: MenuService,
              private readonly translateService: TranslateService,
              private readonly calculationStore: CalculationStore,
              public readonly calculationTitleUpdateFormFactory: CalculationTitleUpdateFormFactory,
              private readonly activatedRoute: ActivatedRoute,
              public modalService: NgbModal,
              private readonly router: Router,
              private readonly loggingService: LoggingService,
              private readonly calculationsUtils: CalculationsUtils,
              private readonly importManager: ImportManager,
              private readonly exportModalFactory: ExportModalFactory,
  ) {
    this.isTitleInEditingMode = false;
  }

  private _calculationStatusEnum: CalculationStatusEnum;

  get calculationStatusEnum(): CalculationStatusEnum {
    return this._calculationStatusEnum;
  }

  set calculationStatusEnum(value: CalculationStatusEnum) {
    if (value !== this._calculationStatusEnum) {
      if (value === CalculationStatusEnum.InProgress) {
        this.startProgress();
      } else {
        this.stopProgress();
      }
    }

    this._calculationStatusEnum = value;
  }

  public _calculationId: string;

  @Input() set calculationId(value: any) {
    this._calculationId = value;
    this.getCalculation();
  }

  ngOnInit(): void {
    this.calculationTitleUpdateForm.lifecycleStatus = FormLifecycleStatusEnum.Loading;

    this.isLoading = true;

    this.getCalculation();
  }

  getTitleFormControl(): UntypedFormControl {
    return this.calculationTitleUpdateForm.get('title') as UntypedFormControl;
  }

  calculateRemainingCalculationTime() {
    const minutes = differenceInMinutes(this.estimatedTimeOfCompletion, new Date());
    const seconds = differenceInSeconds(subMinutes(this.estimatedTimeOfCompletion, minutes), new Date()) + '';
    this.remainingCalculationTime = `${minutes}:${seconds.padStart(2, '0')}`;
  }

  getCalculation() {
    this.calculationSubscription?.unsubscribe();
    this.calculationSubscription = this.calculationStore.get(this._calculationId).subscribe((calculationResponse: CalculationResponse | undefined) => {
      if (calculationResponse === undefined) {
        return;
      }

      this.calculationResponse = calculationResponse;

      // Unsubscribe all the subscriptions on route change.
      this.formServiceOnButtonClickSubscription?.unsubscribe();
      this.calculationErrorsSubscription?.unsubscribe();

      // It's important to init the form since when parameters change, we need to reload it entirely.
      this.calculationTitleUpdateForm.init();

      // This following method registers an observable on any button clicked in the form. Each button has an id
      // and that's how we know which button was clicked on.
      this.formServiceOnButtonClickSubscription = this.calculationTitleUpdateForm.buttonClick$.subscribe(async (button: AdvancedFormButton) => {
        switch (button.id) {
          case 'SAVE':
            await this.saveTitle();
            break;
        }
      });

      this.calculationErrorsSubscription = this.calculationStore.getErrors(this._calculationId).subscribe((validationErrors: {
        property: string,
        messages: string[],
        value: any
      }[]) => {
        this.numberOfErrors = validationErrors?.length ?? 0;
      });

      this.calculationTitleUpdateForm.lifecycleStatus = FormLifecycleStatusEnum.Loading;

      if (calculationResponse.title) {
        this.calculationTitleUpdateForm.setValue({ 'title': calculationResponse.title });
      } else {
        this.calculationTitleUpdateForm.setValue({ 'title': 'Aucun titre défini' });
      }

      this.calculationTitleUpdateForm.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

      this.calculationKeyname = calculationResponse.calculationKeyname;
      this.calculationTitle = this.calculationTitleUpdateForm.value.title;
      this.calculationStatusEnum = calculationResponse.status;

      this.lastModified = this.calculationsUtils.getLastModifiedDate(calculationResponse);

      this.isLoading = false;

      this.accessCurrentlyGranted = this.accessCurrentlyGranted = !(calculationResponse.accessDelegatedToAdminUntil === undefined || calculationResponse.accessDelegatedToAdminUntil < new Date());
      if (this.accessCurrentlyGranted) {
        this.grantedUntil = `${intlFormatDistance(calculationResponse.accessDelegatedToAdminUntil, new Date(), { locale: 'fr-CA' })} (${formatISO9075(calculationResponse.accessDelegatedToAdminUntil)})`;
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  getTitleText(): string {
    return this.calculationsUtils.getCalculationKeynameText(this.calculationKeyname);
  }

  getStatusText(): string {
    return this.calculationsUtils.getStatusText(this.calculationStatusEnum);
  }

  getLastModifiedText() {
    return this.calculationsUtils.getLastModifiedText(this.lastModified);
  }

  toggleTitleEditingMode(): void {
    this.isTitleInEditingMode = !this.isTitleInEditingMode;
  }

  openDelegatedAccessModal() {
    const modal = this.modalService.open(GrantDelegatedAccessModalComponent, {
      size: 'lg',
    });
    (modal.componentInstance as GrantDelegatedAccessModalComponent).calculationId = this.calculationResponse.id;
    modal.closed.subscribe(async result => {
    });

    return;
  }

  async saveTitle() {
    // Todo: Handle errors

    // We want the form to be disabled  automatically so we set everything to isSending so we can't modify anything.
    this.calculationTitleUpdateForm.lifecycleStatus = FormLifecycleStatusEnum.Sending;

    await this.calculationStore.updateTitle(this._calculationId, this.calculationTitleUpdateForm.value);

    // We reset the form to it's initial state after a successful update.
    this.calculationTitleUpdateForm.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

    this.toggleTitleEditingMode();
  }

  openExportModal() {
    this.exportModalFactory.openExportModal(this.calculationResponse);
  }

  openImportModal() {
    const modal = this.modalService.open(ImportModalComponent);
    (modal.componentInstance as ImportModalBase).calculationKeyname = this.calculationKeyname;
    modal.closed.subscribe(async result => {
      if (!result) {
        return;
      }

      // Load the model from the data.
      if (result instanceof CalculationRunOptions && result.calculationKeyname == this.calculationKeyname) {
        this.importManager.importedModel.next(result);
      }
    });

    return;
  }

  async tryDelete(): Promise<void> {
    const modal = this.modalService.open(DeleteCalculationComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.delete();
        return;
      }
    });
  }

  async delete(): Promise<void> {
    await this.calculationStore.delete(this._calculationId);
    await this.router.navigate(['..'], { relativeTo: this.activatedRoute.parent });
  }

  async tryDuplicate(): Promise<void> {
    const modal = this.modalService.open(DuplicateCalculationComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.duplicate();
        return;
      }
    });
  }

  startProgress() {
    this.progress = 0;
    this.estimatedTimeOfCompletion = addSeconds(new Date(), this.progressDurationInSeconds);
    this.progressInterval = setInterval(() => {
      this.progress += 100 / (this.progressDurationInSeconds * 4);
      this.calculateRemainingCalculationTime();
    }, 250);
  }

  stopProgress() {
    this.progress = 0;

    clearInterval(this.progressInterval);
  }

  async duplicate(): Promise<void> {
    const options = new CalculationCreationOptions();
    options.createFromCalculationId = this._calculationId;
    options.calculationKeyname = this.calculationKeyname;
    options.title = this.calculationTitle + ' - Copy';
    const newCalculation = await this.calculationStore.create(options);
    newCalculation.subscribe(async calculation => {
      await this.router.navigate(['..', calculation.calculationKeyname, calculation.id], { relativeTo: this.activatedRoute.parent });
    });
  }

  private unsubscribeAll() {
    this.formServiceOnButtonClickSubscription?.unsubscribe();
    this.calculationErrorsSubscription?.unsubscribe();
    this.calculationSubscription?.unsubscribe();
  }
}
